import React, { useState, useEffect } from 'react';
import { SEO } from '../components/SEO';
import { FaQuestionCircle, FaSearch, FaChevronDown, FaChevronUp, FaWhatsapp } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const FAQ = () => {
  const [isSticky, setIsSticky] = useState(false);
  const [activeSection, setActiveSection] = useState('general');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItems, setExpandedItems] = useState({});

  // Effect for sticky navigation
  useEffect(() => {
    const handleScroll = () => {
      const heroSection = document.getElementById('hero');
      const headerHeight = 64;
      
      if (heroSection) {
        const heroBottom = heroSection.getBoundingClientRect().bottom;
        setIsSticky(heroBottom <= headerHeight);
      } else {
        setIsSticky(true);
      }

      // Set active section based on scroll position
      const sections = ['general', 'products', 'licenses', 'technical', 'purchasing'];
      let activeFound = false;
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          const offset = 140;
          if (rect.top <= offset && rect.bottom >= offset) {
            setActiveSection(section);
            activeFound = true;
            break;
          }
        }
      }
      
      if (!activeFound && window.scrollY > 0) {
        setIsSticky(true);
      }
    };
    
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Function to scroll to sections
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 140;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Toggle FAQ item expansion
  const toggleItem = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // FAQ Data organized by categories
  const faqData = {
    general: [
      {
        id: 'general-1',
        question: '¿Qué es ECO SOFT?',
        answer: 'ECO SOFT es una empresa dedicada al desarrollo de soluciones de software para la industria de la construcción, ofreciendo herramientas para presupuestos, planeación, control y más.'
      },
      {
        id: 'general-2',
        question: '¿Cuánto tiempo lleva ECO SOFT en el mercado?',
        answer: 'ECO SOFT tiene más de 40 años de experiencia desarrollando soluciones especializadas para la industria de la construcción, consolidándose como líder en el sector.'
      },
      {
        id: 'general-3',
        question: '¿Dónde se encuentra ubicada la oficina principal de ECO SOFT?',
        answer: 'Nuestra oficina principal se encuentra en Watteau 54, Nonoalco Mixcoac, Benito Juárez, CP 03700, Ciudad de México.'
      }
    ],
    products: [
      {
        id: 'products-1',
        question: '¿Qué productos ofrece ECO SOFT?',
        answer: 'ECO SOFT ofrece varias soluciones como OPUS (sistema para ingeniería de costos), que incluye módulos de Presupuesto Programable, Planeación y Control, Administración de Compras, Dependencias y Cuantificación BIM. También contamos con INFORTE y OPUS ERP.'
      },
      {
        id: 'products-2',
        question: '¿Cuál es la diferencia entre OPUS 24, OPUS 22 y OPUS 21?',
        answer: 'Las diferentes versiones representan actualizaciones del software, siendo OPUS 24 la versión más reciente con mejoras y funcionalidades adicionales. Cada versión incluye mejoras en rendimiento, nuevas herramientas y corrección de problemas anteriores.'
      },
      {
        id: 'products-3',
        question: '¿En qué sistemas operativos funciona OPUS?',
        answer: 'OPUS está diseñado para funcionar en sistemas Windows. Consulte los requisitos mínimos del sistema para cada versión en nuestra sección de soporte técnico.'
      }
    ],
    licenses: [
      {
        id: 'licenses-1',
        question: '¿Cómo funciona el licenciamiento de OPUS?',
        answer: 'OPUS utiliza un sistema de licenciamiento que puede ser por esquema de renta o perpetuo según su necesidad. Las licencias pueden ser individuales o de red para múltiples usuarios simultáneos.'
      },
      {
        id: 'licenses-2',
        question: '¿Puedo transferir mi licencia a otra computadora?',
        answer: 'Sí, es posible transferir su licencia. El proceso requiere la desactivación en el equipo original antes de activarla en el nuevo equipo. Puede utilizar el Administrador de Licencias para este proceso.'
      },
      {
        id: 'licenses-3',
        question: '¿Qué hacer si perdí mi llave de acceso?',
        answer: 'Si perdió su llave de acceso, debe contactar a nuestro soporte técnico con los datos de su licencia. Podemos ayudar a recuperar el acceso o proporcionar una nueva llave según corresponda.'
      }
    ],
    technical: [
      {
        id: 'technical-1',
        question: '¿Cómo instalo OPUS por primera vez?',
        answer: 'Para instalar OPUS por primera vez, descargue el instalador desde nuestra sección de descargas, ejecute el archivo y siga las instrucciones del asistente de instalación. Después, utilice el Administrador de Licencias para activar su producto.'
      },
      {
        id: 'technical-2',
        question: '¿Qué hacer si tengo problemas durante la instalación?',
        answer: 'Si experimenta problemas durante la instalación, asegúrese de tener permisos de administrador, desactivar temporalmente su antivirus, y verificar que cumple con los requisitos del sistema. Si los problemas persisten, contacte a nuestro soporte técnico.'
      },
      {
        id: 'technical-3',
        question: '¿Cómo puedo actualizar mi versión de OPUS?',
        answer: 'Para actualizar OPUS, descargue la nueva versión desde nuestra sección de descargas y ejecute el instalador. El sistema detectará automáticamente su instalación anterior y actualizará los archivos necesarios.'
      }
    ],
    purchasing: [
      {
        id: 'purchasing-1',
        question: '¿Cómo puedo adquirir sus productos?',
        answer: 'Puede adquirir nuestros productos a través de nuestros distribuidores autorizados o contactándonos directamente. Visite nuestra página de Distribuidores o complete el formulario de contacto.'
      },
      {
        id: 'purchasing-2',
        question: '¿Hay versiones de prueba disponibles?',
        answer: 'Sí, ofrecemos versiones de prueba para la mayoría de nuestros productos. Puede solicitar una versión de prueba contactándonos directamente o a través de nuestros distribuidores.'
      },
      {
        id: 'purchasing-3',
        question: '¿Ofrecen capacitación para sus productos?',
        answer: 'Sí, ofrecemos capacitación para todos nuestros productos. La capacitación puede ser en línea o presencial dependiendo de sus necesidades y ubicación. Contáctenos para más información.'
      }
    ]
  };

  // Filter FAQs based on search term
  const filterFAQs = () => {
    if (!searchTerm.trim()) return faqData;
    
    const filtered = {};
    
    Object.keys(faqData).forEach(category => {
      filtered[category] = faqData[category].filter(item => 
        item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.answer.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
    
    return filtered;
  };
  
  const filteredFAQs = filterFAQs();

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Preguntas Frecuentes | ECO SOFT"
        description="Encuentra respuestas a las preguntas más frecuentes sobre nuestros productos y servicios."
        name="FAQ"
        type="article"
      />
      
      {/* Hero Section */}
      <section id="hero" className="relative h-[450px] bg-gradient-to-r from-[#003171] to-[#0066CC]">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="container mx-auto px-4 h-full flex items-center pt-12 relative z-10">
          <div className="max-w-2xl text-white">
            <h1 className="text-5xl font-bold mb-4">Preguntas Frecuentes</h1>
            <p className="text-xl mb-8">
              Encuentra respuestas a las dudas más comunes sobre nuestros productos, servicios y soporte técnico.
            </p>
            <div className="relative max-w-lg">
              <input
                type="text"
                placeholder="Buscar preguntas frecuentes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/90 border-none rounded-md text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#0066CC]"
              />
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500" />
            </div>
          </div>
        </div>
      </section>
      
      {/* Navigation container with fixed height to prevent content jumps */}
      <div 
        className={`bg-white shadow-md transition-all duration-300 sticky-nav ${
          isSticky 
            ? 'fixed top-[64px] left-0 right-0 z-30 border-t border-gray-200' 
            : 'relative'
        }`}
        style={{ minHeight: '56px' }}
      >
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex overflow-x-auto py-4 items-center relative">
            <div className="flex-grow flex">
              <button 
                onClick={() => scrollToSection('general')}
                className={`px-6 py-2 whitespace-nowrap font-medium ${
                  activeSection === 'general' 
                    ? 'text-[#0066CC] border-b-2 border-[#0066CC]' 
                    : 'text-gray-600 hover:text-[#0066CC]'
                }`}
              >
                General
              </button>
              <button 
                onClick={() => scrollToSection('products')}
                className={`px-6 py-2 whitespace-nowrap font-medium ${
                  activeSection === 'products' 
                    ? 'text-[#0066CC] border-b-2 border-[#0066CC]' 
                    : 'text-gray-600 hover:text-[#0066CC]'
                }`}
              >
                Productos
              </button>
              <button 
                onClick={() => scrollToSection('licenses')}
                className={`px-6 py-2 whitespace-nowrap font-medium ${
                  activeSection === 'licenses' 
                    ? 'text-[#0066CC] border-b-2 border-[#0066CC]' 
                    : 'text-gray-600 hover:text-[#0066CC]'
                }`}
              >
                Licencias
              </button>
              <button 
                onClick={() => scrollToSection('technical')}
                className={`px-6 py-2 whitespace-nowrap font-medium ${
                  activeSection === 'technical' 
                    ? 'text-[#0066CC] border-b-2 border-[#0066CC]' 
                    : 'text-gray-600 hover:text-[#0066CC]'
                }`}
              >
                Técnico
              </button>
              <button 
                onClick={() => scrollToSection('purchasing')}
                className={`px-6 py-2 whitespace-nowrap font-medium ${
                  activeSection === 'purchasing' 
                    ? 'text-[#0066CC] border-b-2 border-[#0066CC]' 
                    : 'text-gray-600 hover:text-[#0066CC]'
                }`}
              >
                Compras
              </button>
            </div>
            <div className="flex items-center ml-4">
              <FaQuestionCircle className="text-[#0066CC] text-xl" />
            </div>
          </div>
        </div>
      </div>
      
      {/* FAQ Content */}
      <div className="py-12">
        <div className="container mx-auto px-4">
          {/* General FAQs */}
          <section id="general" className="mb-16">
            <h2 className="text-3xl font-bold mb-8">Preguntas Generales</h2>
            <div className="space-y-4 max-w-3xl">
              {filteredFAQs.general && filteredFAQs.general.length > 0 ? (
                filteredFAQs.general.map(item => (
                  <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                    <button
                      onClick={() => toggleItem(item.id)}
                      className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 focus:outline-none"
                    >
                      <h3 className="text-lg font-semibold">{item.question}</h3>
                      {expandedItems[item.id] ? <FaChevronUp className="text-[#0066CC]" /> : <FaChevronDown className="text-[#0066CC]" />}
                    </button>
                    {expandedItems[item.id] && (
                      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                        <p className="text-gray-700">{item.answer}</p>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-gray-500">No se encontraron preguntas que coincidan con su búsqueda.</p>
              )}
            </div>
          </section>
          
          {/* Products FAQs */}
          <section id="products" className="mb-16">
            <h2 className="text-3xl font-bold mb-8">Productos</h2>
            <div className="space-y-4 max-w-3xl">
              {filteredFAQs.products && filteredFAQs.products.length > 0 ? (
                filteredFAQs.products.map(item => (
                  <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                    <button
                      onClick={() => toggleItem(item.id)}
                      className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 focus:outline-none"
                    >
                      <h3 className="text-lg font-semibold">{item.question}</h3>
                      {expandedItems[item.id] ? <FaChevronUp className="text-[#0066CC]" /> : <FaChevronDown className="text-[#0066CC]" />}
                    </button>
                    {expandedItems[item.id] && (
                      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                        <p className="text-gray-700">{item.answer}</p>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-gray-500">No se encontraron preguntas que coincidan con su búsqueda.</p>
              )}
            </div>
          </section>
          
          {/* Licenses FAQs */}
          <section id="licenses" className="mb-16">
            <h2 className="text-3xl font-bold mb-8">Licencias</h2>
            <div className="space-y-4 max-w-3xl">
              {filteredFAQs.licenses && filteredFAQs.licenses.length > 0 ? (
                filteredFAQs.licenses.map(item => (
                  <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                    <button
                      onClick={() => toggleItem(item.id)}
                      className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 focus:outline-none"
                    >
                      <h3 className="text-lg font-semibold">{item.question}</h3>
                      {expandedItems[item.id] ? <FaChevronUp className="text-[#0066CC]" /> : <FaChevronDown className="text-[#0066CC]" />}
                    </button>
                    {expandedItems[item.id] && (
                      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                        <p className="text-gray-700">{item.answer}</p>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-gray-500">No se encontraron preguntas que coincidan con su búsqueda.</p>
              )}
            </div>
          </section>
          
          {/* Technical FAQs */}
          <section id="technical" className="mb-16">
            <h2 className="text-3xl font-bold mb-8">Soporte Técnico</h2>
            <div className="space-y-4 max-w-3xl">
              {filteredFAQs.technical && filteredFAQs.technical.length > 0 ? (
                filteredFAQs.technical.map(item => (
                  <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                    <button
                      onClick={() => toggleItem(item.id)}
                      className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 focus:outline-none"
                    >
                      <h3 className="text-lg font-semibold">{item.question}</h3>
                      {expandedItems[item.id] ? <FaChevronUp className="text-[#0066CC]" /> : <FaChevronDown className="text-[#0066CC]" />}
                    </button>
                    {expandedItems[item.id] && (
                      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                        <p className="text-gray-700">{item.answer}</p>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-gray-500">No se encontraron preguntas que coincidan con su búsqueda.</p>
              )}
            </div>
          </section>
          
          {/* Purchasing FAQs */}
          <section id="purchasing" className="mb-16">
            <h2 className="text-3xl font-bold mb-8">Compras</h2>
            <div className="space-y-4 max-w-3xl">
              {filteredFAQs.purchasing && filteredFAQs.purchasing.length > 0 ? (
                filteredFAQs.purchasing.map(item => (
                  <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                    <button
                      onClick={() => toggleItem(item.id)}
                      className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 focus:outline-none"
                    >
                      <h3 className="text-lg font-semibold">{item.question}</h3>
                      {expandedItems[item.id] ? <FaChevronUp className="text-[#0066CC]" /> : <FaChevronDown className="text-[#0066CC]" />}
                    </button>
                    {expandedItems[item.id] && (
                      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                        <p className="text-gray-700">{item.answer}</p>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-gray-500">No se encontraron preguntas que coincidan con su búsqueda.</p>
              )}
            </div>
          </section>
        </div>
      </div>
      
      {/* CTA Section */}
      <section className="py-16 bg-[#1A2B39] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">¿No encontraste lo que buscabas?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Nuestro equipo de soporte técnico está disponible para responder todas tus preguntas.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://wa.me/525539908312" 
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-3 bg-[#0066CC] hover:bg-[#0088FF] text-white font-medium rounded-md transition-colors flex items-center"
            >
              <FaWhatsapp className="mr-2" /> Contactar Soporte
            </a>
            <Link 
              to="/soporte/descargas" 
              className="px-8 py-3 border border-white hover:bg-white hover:text-[#1A2B39] text-white font-medium rounded-md transition-colors"
            >
              Centro de Descargas
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ; 